import { OPENAI_MODEL_CATALOG } from './openAiModelCatalog';
import type { LlmProfileModelStatus } from './types';

/** What a provider puts in front of every OpenAI model id; empty/absent means plain ids. */
export type ModelIdPrefix = string | null | undefined;

const PLAIN_IDS: ReadonlySet<string> = new Set(
  OPENAI_MODEL_CATALOG.flatMap((tier) => tier.models.map((model) => model.id)),
);

/** The id the provider accepts for a catalog model: the backend's prefix, then the plain id. */
export function toDisplayedId(plainId: string, prefix: ModelIdPrefix): string {
  return `${prefix ?? ''}${plainId}`;
}

/** The plain catalog id behind `model` as shown under `prefix`, or `null` if it is not a catalog model there. */
export function toPlainId(model: string, prefix: ModelIdPrefix): string | null {
  const head = prefix ?? '';
  if (!model.startsWith(head)) return null;
  const plain = model.slice(head.length);
  return PLAIN_IDS.has(plain) ? plain : null;
}

/**
 * Re-express a catalog model for a different provider's prefix.
 *
 * `null` when `model` is not a catalog model under `previousPrefix` (hand-typed,
 * or already wrong) — the caller keeps it and warns instead of guessing.
 */
export function convertModelForPrefix(
  model: string,
  previousPrefix: ModelIdPrefix,
  nextPrefix: ModelIdPrefix,
): string | null {
  const plain = toPlainId(model, previousPrefix);
  return plain === null ? null : toDisplayedId(plain, nextPrefix);
}

export type StoredModelNotice =
  { kind: 'removed' } | { kind: 'use-id'; id: string } | { kind: 'unknown' };

/** What to tell the user about a stored model, from the backend's verdict. `null` when it is valid. */
export function diagnoseStoredModel(
  status: LlmProfileModelStatus,
): StoredModelNotice | null {
  if (status.valid) return null;
  if (status.expected_model)
    return { kind: 'use-id', id: status.expected_model };
  return status.reason === 'removed'
    ? { kind: 'removed' }
    : { kind: 'unknown' };
}
